import * as React from 'react';
import { SelectionPillWidget } from '../../../widgets/SelectionPillWidget';
import { Link, useSearchParams } from 'react-router';
import { SiteRoute } from '@journeyapps/package-types';
import { PageWidget } from '../../../widgets/layout/PageWidget';
import {
  FEATURE_GOALS,
  FEATURE_LANGUAGES,
  FEATURE_TEAM_OPTIONS,
  FEATURE_GOVERNANCE_OPTIONS,
  FeatureGoalId,
  getAvailableTraits,
  getAvailableBuildMethods
} from '../../../data/featureWizard';
import { S } from './FeatureExplorerStyles';
import { PlatformFeaturesWidget } from './PlatformFeaturesWidget';
import { FeatureSelection, readFeatureSelection, featureSelectionParams } from './featureSelection';

enum WizardStep {
  GOALS = 'goals',
  TRAITS = 'traits',
  METHODS = 'methods',
  LANGUAGE = 'language',
  TEAM = 'team',
  RESULTS = 'results'
}

const STEPS = [
  { id: WizardStep.GOALS, label: 'Your goal' },
  { id: WizardStep.TRAITS, label: 'What it needs to do' },
  { id: WizardStep.METHODS, label: 'How you want to build' },
  { id: WizardStep.LANGUAGE, label: 'Language' },
  { id: WizardStep.TEAM, label: 'Team & governance' }
];

export const FeatureFinderPage: React.FC = () => {
  const [params, setParams] = useSearchParams();
  const selection = readFeatureSelection(params);
  const { goals, traits, methods } = selection;
  const requestedStep = params.get('step') as WizardStep;
  const step = goals.length && Object.values(WizardStep).includes(requestedStep) ? requestedStep : WizardStep.GOALS;
  const heading = React.useRef<HTMLHeadingElement>(null);
  const activeStepButton = React.useRef<HTMLButtonElement>(null);
  const previousStep = React.useRef(step);
  React.useEffect(() => {
    if (previousStep.current !== step) {
      (step === WizardStep.RESULTS ? heading.current : activeStepButton.current)?.focus({ preventScroll: true });
      previousStep.current = step;
    }
  }, [step]);
  const update = (nextSelection: FeatureSelection, nextStep: WizardStep, replace = true) => {
    const next = featureSelectionParams(nextSelection);
    next.set('step', nextStep);
    setParams(next, { replace });
  };
  const goToStep = (nextStep: WizardStep) => update(selection, nextStep, false);
  const toggleGoal = (id: FeatureGoalId) => {
    const nextGoals = goals.includes(id) ? goals.filter((goal) => goal !== id) : [...goals, id];
    const validTraits = new Set(getAvailableTraits(nextGoals).map((option) => option.id));
    const validMethods = new Set(getAvailableBuildMethods(nextGoals).map((option) => option.id));
    update(
      {
        ...selection,
        goals: nextGoals,
        traits: traits.filter((id) => validTraits.has(id)),
        methods: methods.filter((id) => validMethods.has(id)),
        query: ''
      },
      WizardStep.GOALS
    );
  };

  return (
    <PageWidget width={1200} title="Help me explore">
      <S.Intro>
        <Link to={SiteRoute.FEATURES}>← All technologies and features</Link>
        <h1>Help me explore</h1>
        <p>Tell us what you want to achieve, what your solution needs to do, and how you want to build it.</p>
      </S.Intro>
      {step === WizardStep.RESULTS ? (
        <PlatformFeaturesWidget
          selection={selection}
          headingRef={heading}
          onSelectionChange={(next) => update(next, WizardStep.RESULTS)}
        />
      ) : (
        <S.Explorer>
          <S.Steps aria-label="Progress">
            {STEPS.map((item, index) => (
              <li key={item.id}>
                <button
                  type="button"
                  ref={item.id === step ? activeStepButton : undefined}
                  aria-current={item.id === step ? 'step' : undefined}
                  disabled={item.id !== WizardStep.GOALS && !goals.length}
                  onClick={() => goToStep(item.id)}
                >
                  {index + 1}. {item.label}
                </button>
              </li>
            ))}
          </S.Steps>
          {step === WizardStep.GOALS ? (
            <>
              <S.Goals aria-label="Choose one or more goals">
                <S.GoalGrid>
                  {FEATURE_GOALS.map((goal) => (
                    <SelectionPillWidget
                      key={goal.id}
                      title={goal.description}
                      type="checkbox"
                      checked={goals.includes(goal.id)}
                      onChange={() => toggleGoal(goal.id)}
                    >
                      {goal.name}
                    </SelectionPillWidget>
                  ))}
                </S.GoalGrid>
              </S.Goals>
              <S.Actions>
                <S.Next type="button" disabled={!goals.length} onClick={() => goToStep(WizardStep.TRAITS)}>
                  Continue
                </S.Next>
              </S.Actions>
            </>
          ) : step === WizardStep.TRAITS ? (
            <>
              <S.Goals aria-label="What it needs to do">
                <S.GoalGrid>
                  {getAvailableTraits(goals).map((option) => (
                    <SelectionPillWidget
                      key={option.id}
                      title={option.description}
                      type="checkbox"
                      checked={traits.includes(option.id)}
                      onChange={() =>
                        update(
                          {
                            ...selection,
                            traits: traits.includes(option.id)
                              ? traits.filter((id) => id !== option.id)
                              : [...traits, option.id]
                          },
                          WizardStep.TRAITS
                        )
                      }
                    >
                      {option.name}
                    </SelectionPillWidget>
                  ))}
                </S.GoalGrid>
              </S.Goals>
              <S.Actions>
                <S.Next type="button" onClick={() => goToStep(WizardStep.METHODS)}>
                  Continue
                </S.Next>
              </S.Actions>
            </>
          ) : step === WizardStep.METHODS ? (
            <>
              <S.Goals aria-label="How you want to build">
                <S.GoalGrid>
                  {getAvailableBuildMethods(goals).map((option) => (
                    <SelectionPillWidget
                      key={option.id}
                      title={option.description}
                      type="checkbox"
                      checked={methods.includes(option.id)}
                      onChange={() =>
                        update(
                          {
                            ...selection,
                            methods: methods.includes(option.id)
                              ? methods.filter((id) => id !== option.id)
                              : [...methods, option.id]
                          },
                          WizardStep.METHODS
                        )
                      }
                    >
                      {option.name}
                    </SelectionPillWidget>
                  ))}
                </S.GoalGrid>
              </S.Goals>
              <S.Actions>
                <S.Next type="button" onClick={() => goToStep(WizardStep.LANGUAGE)}>
                  Continue
                </S.Next>
              </S.Actions>
            </>
          ) : step === WizardStep.LANGUAGE ? (
            <>
              <S.Goals aria-label="Choose JavaScript or TypeScript">
                <S.GoalGrid>
                  {FEATURE_LANGUAGES.map((option) => (
                    <SelectionPillWidget
                      key={option.id}
                      type="radio"
                      name="language"
                      checked={selection.language === option.id}
                      onChange={() => update({ ...selection, language: option.id }, WizardStep.LANGUAGE)}
                    >
                      {option.name}
                    </SelectionPillWidget>
                  ))}
                </S.GoalGrid>
              </S.Goals>
              <S.Actions>
                <S.Next type="button" disabled={!selection.language} onClick={() => goToStep(WizardStep.TEAM)}>
                  Continue
                </S.Next>
              </S.Actions>
            </>
          ) : (
            <>
              <S.Goals aria-label="Are you building on your own or in a team?">
                <S.GoalGrid>
                  {FEATURE_TEAM_OPTIONS.map((option) => (
                    <SelectionPillWidget
                      key={option.id}
                      type="radio"
                      name="team"
                      checked={selection.team === option.id}
                      onChange={() => update({ ...selection, team: option.id }, WizardStep.TEAM)}
                    >
                      {option.name}
                    </SelectionPillWidget>
                  ))}
                </S.GoalGrid>
              </S.Goals>
              <S.Goals aria-label="Governance requirements">
                <S.GoalGrid>
                  {FEATURE_GOVERNANCE_OPTIONS.map((option) => (
                    <SelectionPillWidget
                      key={option.id}
                      title={option.description}
                      type="checkbox"
                      checked={selection.governance.includes(option.id)}
                      onChange={() =>
                        update(
                          {
                            ...selection,
                            governance: selection.governance.includes(option.id)
                              ? selection.governance.filter((id) => id !== option.id)
                              : [...selection.governance, option.id]
                          },
                          WizardStep.TEAM
                        )
                      }
                    >
                      {option.name}
                    </SelectionPillWidget>
                  ))}
                </S.GoalGrid>
              </S.Goals>
              <S.Actions>
                <S.Next
                  type="button"
                  disabled={!selection.team || !selection.language}
                  onClick={() => goToStep(WizardStep.RESULTS)}
                >
                  Show my platform →
                </S.Next>
              </S.Actions>
            </>
          )}
        </S.Explorer>
      )}
    </PageWidget>
  );
};
